import type { FormType } from "@/types/entities/formality-request";
import type { ApiFlowsExecutionsResponseType } from "@/types/entities/flows";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

/**
 * Get the text of the last comment in the last correction of the last revision
 * @param data - FormType object containing form assignments and revisions
 * @returns The text of the last comment or undefined if not found
 */
export const getLastRevisionComments = (data: FormType) => {
  // Check if we have form assignments
  if (
    !data?.form_assignments?.length ||
    !data.form_assignments[0]?.revisions?.length
  )
    return undefined;

  // Get the last revision instead of the first one (index 0)
  const revisions = data.form_assignments[0].revisions;
  const lastRevision = revisions[revisions.length - 1];

  // If there are no corrections, return undefined
  if (!lastRevision.corrections?.length) return undefined;

  // Get the last correction
  const lastCorrection =
    lastRevision.corrections[lastRevision.corrections.length - 1];

  // If there are no comments, return undefined
  if (!lastCorrection.comments?.length) return undefined;

  // Get the last comment
  const lastComment =
    lastCorrection.comments[lastCorrection.comments.length - 1];
  return lastComment.text;
};

/**
 * Parse a date string that may or may not have timezone information
 * @param dateString - Date string in ISO format (e.g. 2025-10-07T00:43:44.000000Z)
 * @returns A valid Date object or null if invalid
 */
export const parseDeadlineDate = (dateString?: string): Date | undefined => {
  if (!dateString) return;

  try {
    // Remove microseconds if present for consistent parsing
    const normalizedString = dateString.replace(/(\.\d{6})\d*/, "$1");

    // Check if the string has timezone information (Z or +/-XX:XX)
    const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(normalizedString);

    // Parse the date string
    const date = new Date(normalizedString);

    // Check if the date is valid
    if (!isNaN(date.getTime())) {
      return date;
    }

    // If date is invalid and doesn't have timezone, try adding Z
    if (!hasTimezone) {
      const dateWithZ = new Date(normalizedString + "Z");
      if (!isNaN(dateWithZ.getTime())) {
        return dateWithZ;
      }
    }

    return undefined;
  } catch (error) {
    console.error("Error parsing deadline date:", error);
    return;
  }
};

export const getInternalDeadlineFromFormData = (
  data?: Partial<{
    form_assignments?: Array<{
      revisions?: Array<{ internal_deadline: string | null }>;
    }>;
  }>,
): Date | undefined => {
  // Check if we have form assignments
  if (!data) return undefined;
  if (
    !data?.form_assignments?.length ||
    !data.form_assignments[0]?.revisions?.length
  )
    return undefined;

  // Get the last revision instead of the first one (index 0)
  const revisions = data.form_assignments[0].revisions;
  const revision = revisions[revisions.length - 1];
  const internal_deadline = revision.internal_deadline;

  if (internal_deadline) {
    return parseDeadlineDate(internal_deadline);
  }
};

export const getExternalDeadlineFromFormData = (
  data?: Partial<
    {
      revision?: { external_deadline: string | null };
    }[]
  >,
): Date | undefined => {
  if (!data) return undefined;
  if (!data?.length || !data[0]?.revision?.external_deadline) return undefined;

  // Get the last revision instead of the first one (index 0)
  const revision = data[data.length - 1]?.revision;
  const external_deadline = revision?.external_deadline;

  if (external_deadline) {
    return parseDeadlineDate(external_deadline);
  }
};

/**
 * Check if the timer should be disabled based on internal_started_at
 * @param data - Form data containing form assignments and revisions
 * @returns true if internal_started_at exists and timer should be disabled
 */
export const isTimerDisabled = (
  data?: Partial<{
    form_assignments?: Array<{
      revisions?: Array<{
        internal_started_at?: string | null;
        internal_deadline?: string | null;
      }>;
    }>;
  }>,
): boolean => {
  if (!data?.form_assignments?.length) return false;

  const formAssignment = data.form_assignments[0];
  if (!formAssignment?.revisions?.length) return false;

  // Get the last revision
  const lastRevision =
    formAssignment.revisions[formAssignment.revisions.length - 1];

  // Return true if internal_started_at exists (timer should be disabled)
  return !!lastRevision?.internal_started_at;
};

/**
 * Get the internal_started_at date from form data
 * @param data - Form data containing form assignments and revisions
 * @returns Date object for internal_started_at or undefined if not available
 */
export const getInternalStartedAtFromFormData = (
  data?: Partial<{
    form_assignments?: Array<{
      revisions?: Array<{
        internal_started_at?: string | null;
      }>;
    }>;
  }>,
): Date | undefined => {
  if (!data?.form_assignments?.length) return undefined;

  const formAssignment = data.form_assignments[0];
  if (!formAssignment?.revisions?.length) return undefined;

  // Get the last revision
  const lastRevision =
    formAssignment.revisions[formAssignment.revisions.length - 1];

  // Return parsed date if internal_started_at exists
  return lastRevision?.internal_started_at
    ? parseDeadlineDate(lastRevision.internal_started_at)
    : undefined;
};

/**
 * Calculate the time remaining between now and a target date
 * @param date - The target date to calculate time remaining until
 * @returns A formatted string representing the time remaining (e.g., "1h 30m 0s", "31m 14s")
 */
export const getRemainingTime = (
  date?: Date,
  showSeconds: boolean = true,
): string => {
  // Check if date is valid
  if (!date) return "";

  const now = new Date();

  // If target date is in the past, return empty string
  if (date <= now) {
    return "";
  }

  // Calculate the time difference in milliseconds
  const diffMs = date.getTime() - now.getTime();

  // Convert to time units
  const totalSeconds = Math.floor(diffMs / 1000);
  const d = Math.floor(totalSeconds / (60 * 60 * 24));
  const h = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const m = Math.floor((totalSeconds % (60 * 60)) / 60);
  const s = Math.floor(totalSeconds % 60);

  // Format string according to requirements
  const parts = [];
  // Add days only if present
  if (d > 0) parts.push(`${d}d`);
  // Add hours only if present
  if (d > 0 || h > 0) parts.push(`${h}h`);
  // Always add minutes and seconds
  if (h > 0 || m > 0) parts.push(`${m}m`);
  if (showSeconds) parts.push(`${s}s`);
  return parts.join(" ");
};

export const getElapsedTime = (date?: Date): string => {
  const now = dayjs();
  const then = dayjs(date);
  const diff = now.diff(then);

  const dur = dayjs.duration(diff);
  const d = Math.floor(dur.asDays());
  const h = dur.hours();
  const m = dur.minutes();

  // Format string according to requirements
  const parts = [];
  // Add days only if present
  if (d > 0) parts.push(`${d}d`);
  // Add hours only if present
  if (d > 0 || h > 0) parts.push(`${h}h`);
  // Always add minutes and seconds
  if (h > 0 || m > 0) parts.push(`${m}m`);
  return parts.join(" ");
};

export const getRemainingTimeStr = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return getRemainingTime(date);
  } catch (error) {
    console.error("Error parsing deadline date:", error);
    return "";
  }
  return "";
};

type NodeExecution =
  ApiFlowsExecutionsResponseType["data"][number]["node_executions"][number];

/**
 * Determina si se debe mostrar el botón de continuar tramite y obtiene el payment_request_id
 * @param uniqueExecutionNodes - Array de node executions únicos ordenados por node.order
 * @param flowExecutionStatus - Estado de la ejecución del flujo
 * @returns Objeto con shouldShowContinueButton y paymentRequestId
 */
export const shouldShowContinueButton = (
  uniqueExecutionNodes: NodeExecution[],
  flowExecutionStatus?: string,
): {
  shouldShowContinueButton: boolean;
  paymentRequestId: string | undefined;
} => {
  if (uniqueExecutionNodes.length === 0) {
    return { shouldShowContinueButton: false, paymentRequestId: undefined };
  }

  const flowExecutionIsPending = flowExecutionStatus === "PENDING";
  const flowExecutionIsRunningOrCompleted =
    flowExecutionStatus === "RUNNING" || flowExecutionStatus === "COMPLETED";

  // El último nodo en la lista ordenada por node.order
  const lastNode = uniqueExecutionNodes[uniqueExecutionNodes.length - 1];
  const nodeExecutionIsPending = lastNode.status === "PENDING";
  const nodeExecutionIsCompleted = lastNode.status === "COMPLETED";
  const nodeHasManualContinue = lastNode.node?.manual_continue === true;
  const isPaymentNode = lastNode.node?.ref_name === "payment-node";

  const paymentRequestIsCompleted = lastNode.payment_request?.status === "PAID";
  const paymentConditionMet = isPaymentNode
    ? paymentRequestIsCompleted
    : !!lastNode.payment_request?.id;

  const showConditionsMet =
    flowExecutionIsPending &&
    nodeExecutionIsPending &&
    nodeHasManualContinue &&
    paymentConditionMet;

  // Condiciones para ocultar el botón:
  // - (Flow execution es RUNNING o COMPLETED) Y (node execution es COMPLETED) Y (manual_continue = false)
  const hideConditionsMet =
    flowExecutionIsRunningOrCompleted &&
    nodeExecutionIsCompleted &&
    !nodeHasManualContinue;

  const shouldShow = showConditionsMet && !hideConditionsMet;

  return {
    shouldShowContinueButton: shouldShow,
    paymentRequestId: lastNode.payment_request?.id ?? undefined,
  };
};
